import React from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const TermsConditions = () => {
  const magentaColor = '#d100a3';
  const lastUpdated = 'December 29, 2025';

  return (
    <LegalLayout app="ClipPilot" docType="Terms & Conditions" icon={FileText} lastUpdated={lastUpdated}>
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Agreement to Terms
              </h2>
              <p className="mb-4 leading-relaxed">
                By downloading, installing, or using ClipPilot ("the App"), you agree to be bound
                by these Terms and Conditions ("Terms"). If you do not agree to these Terms, do
                not use the App.
              </p>
              <p className="leading-relaxed">
                These Terms constitute a legally binding agreement between you and Feral Techlab
                ("we," "our," or "us") regarding your use of the App.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Description of Service
              </h2>
              <p className="mb-4 leading-relaxed">
                ClipPilot is a clipboard history manager that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Captures and stores text content copied to your device's clipboard</li>
                <li>Automatically classifies content (OTPs, URLs, emails, phone numbers, addresses, code snippets)</li>
                <li>Provides a keyboard extension for quick access to saved clips</li>
                <li>Offers optional iCloud sync for Pro subscribers</li>
                <li>Integrates with Siri Shortcuts for automation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                License Grant
              </h2>
              <p className="mb-4 leading-relaxed">
                Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
                non-transferable, revocable license to download, install, and use the App for your
                personal, non-commercial purposes on any Apple-branded device that you own or control.
              </p>
              <p className="leading-relaxed">
                This license does not allow you to use the App on any device that you do not own
                or control, and you may not distribute or make the App available over a network
                where it could be used by multiple devices at the same time.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                User Responsibilities
              </h2>
              <p className="mb-4 leading-relaxed">
                You are solely responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The content you copy and store using the App</li>
                <li>Ensuring the security of your PIN and biometric access</li>
                <li>Backing up any important data stored in the App</li>
                <li>Complying with all applicable laws when using the App</li>
                <li>Managing sensitive information (passwords, financial data) with appropriate care</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Keyboard Extension
              </h2>
              <p className="mb-4 leading-relaxed">
                The ClipPilot keyboard extension requires "Full Access" permission to function.
                By enabling Full Access, you acknowledge that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The keyboard can access saved clipboard items from the main app</li>
                <li>The keyboard does NOT log, record, or transmit your keystrokes</li>
                <li>The keyboard only reads shared data from the ClipPilot app group</li>
                <li>You can revoke Full Access at any time in iOS Settings</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Restrictions
              </h2>
              <p className="mb-4 leading-relaxed">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Copy, modify, or distribute the App or any portion thereof</li>
                <li>Reverse engineer, decompile, or disassemble the App</li>
                <li>Rent, lease, lend, sell, or sublicense the App</li>
                <li>Use the App to store illegal, harmful, or malicious content</li>
                <li>Attempt to circumvent the App's security features</li>
                <li>Use the App for any unlawful purpose or in violation of applicable laws</li>
                <li>Remove, alter, or obscure any proprietary notices on the App</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Pro Subscription & In-App Purchases
              </h2>
              <p className="mb-4 leading-relaxed">
                ClipPilot offers optional Pro features through in-app purchases:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong style={{ color: 'var(--text-primary)' }}>Lifetime Pro:</strong> One-time purchase for unlimited clips and iCloud sync</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Yearly Pro:</strong> Auto-renewable subscription for cloud features</li>
              </ul>
              <p className="mb-4 leading-relaxed">
                All purchases are processed through the Apple App Store. By making a purchase, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Apple's Terms and Conditions for in-app purchases</li>
                <li>Auto-renewal of subscriptions unless cancelled at least 24 hours before the current period ends</li>
                <li>Managing subscriptions through your Apple ID account settings</li>
              </ul>
              <p className="mt-4 leading-relaxed">
                <strong style={{ color: 'var(--text-primary)' }}>Refunds:</strong> All refund requests
                must be directed to Apple through the App Store, as we do not process payments directly.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                iCloud Sync
              </h2>
              <p className="mb-4 leading-relaxed">
                If you enable iCloud sync (Pro feature):
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Data is synced using Apple's CloudKit service</li>
                <li>Sync is subject to Apple's iCloud Terms of Service</li>
                <li>We are not responsible for iCloud service availability or data loss</li>
                <li>You are responsible for having adequate iCloud storage</li>
                <li>Conflict resolution uses a "last write wins" strategy</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Intellectual Property
              </h2>
              <p className="leading-relaxed">
                The App, including all content, features, and functionality, is owned by Feral
                Techlab and is protected by international copyright, trademark, patent, trade
                secret, and other intellectual property laws. Our trademarks may not be used
                without our prior written consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Loss Disclaimer
              </h2>
              <p className="leading-relaxed">
                While we strive to provide a reliable service, we cannot guarantee against data
                loss. You acknowledge that clipboard data may be lost due to device failure, iOS
                updates, app updates, or other unforeseen circumstances. We strongly recommend
                that you do not rely solely on ClipPilot for storing critical information and
                maintain backups of important data through other means.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Disclaimer of Warranties
              </h2>
              <p className="mb-4 leading-relaxed">
                THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY
                KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES
                OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p className="leading-relaxed">
                We do not warrant that the App will be uninterrupted, error-free, or free of
                viruses or other harmful components. You assume all responsibility and risk for
                your use of the App.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Limitation of Liability
              </h2>
              <p className="leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL FERAL
                TECHLAB BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
                PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA, USE, GOODWILL, OR
                OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OR INABILITY TO USE THE APP,
                INCLUDING BUT NOT LIMITED TO LOSS OF CLIPBOARD DATA, UNAUTHORIZED ACCESS TO
                YOUR DATA, OR ANY INTERRUPTION OF SERVICE.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Indemnification
              </h2>
              <p className="leading-relaxed">
                You agree to indemnify, defend, and hold harmless Feral Techlab and its officers,
                directors, employees, and agents from and against any claims, liabilities, damages,
                losses, and expenses, including reasonable attorneys' fees, arising out of or in
                any way connected with your use of the App, your violation of these Terms, or
                your violation of any rights of another.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Termination
              </h2>
              <p className="leading-relaxed">
                We may terminate or suspend your access to the App immediately, without prior
                notice or liability, for any reason, including if you breach these Terms. Upon
                termination, your right to use the App will immediately cease. You may also
                terminate this agreement at any time by uninstalling the App from your device.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to Terms
              </h2>
              <p className="leading-relaxed">
                We reserve the right to modify these Terms at any time. If we make material
                changes, we will notify you by updating the "Last updated" date at the top of
                these Terms. Your continued use of the App after any such changes constitutes
                your acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Governing Law
              </h2>
              <p className="leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of
                the jurisdiction in which Feral Techlab operates, without regard to its conflict
                of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:support@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: magentaColor }}
                  >
                    support@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/clippilot/support"
                    className="transition-colors"
                    style={{ color: magentaColor }}
                  >
                    ClipPilot Support Page
                  </Link>
                </p>
              </div>
            </section>
          </div>
    </LegalLayout>
  );
};

export default TermsConditions;
